import React, { useState, useEffect, useRef } from "react";
import ReportsHelpers from "../../utils/ReportsHelpers";
import ReportCount from "../reports/ReportCount";
import ReportChart from "../reports/ReportChart";
import ReportConversionRateChart from "../reports/ReportConversionRateChart";
import ReportFunnelChart from "../reports/ReportFunnelChart";
import updateAOV from "../../utils/updateAOV";
import updateConversionRate from "../../utils/updateConversionRate";
import Tooltip from "../shared/Tooltip";
import { reportingGoals } from "../../src/reportingGoals";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
library.add(fab, fas, far);
import { ColumnChart } from "react-chartkick";
import "chartkick/chart.js";
import readReportCount from "../../utils/readReportCount";
import {
  utcDayStart,
  utcDayEnd,
  utcDayLabel,
  pickerDate,
} from "../../utils/utcDayBoundary";

const WebsiteDashboardReport = (props) => {
  const website = props.website;
  const goals = reportingGoals(website);
  const [metric, setMetric] = useState("conversions");
  const [analysisType, setAnalysisType] = useState("funnel");
  const [timeframeType, setTimeframeType] = useState("Last 30 Days");
  const [rangeStartValue, setRangeStartValue] = useState(
    props.timeframeTypes.filter((t) => t.value == "Last 24 Hours")[0]
      .range_start
  );
  const [rangeEndValue, setRangeEndValue] = useState(
    props.timeframeTypes.filter((t) => t.value == "Last 24 Hours")[0].range_end
  );
  const rangeStartInput = useRef(null);
  const rangeEndInput = useRef(null);
  const [revenueType, setRevenueType] = useState("attributed");
  const [currentAOV, setCurrentAOV] = useState(0);
  const [comparisonAOV, setComparisonAOV] = useState(0);
  const [aovPercentageChange, setAovPercentageChange] = useState(null);
  const [storeAOV, setStoreAOV] = useState(0);
  const [storeAOVLastMonth, setStoreAOVLastMonth] = useState(0);
  const [storeAovPercentageChange, setStoreAovPercentageChange] =
    useState(null);
  const [aovLiftIsHovered, setAovLiftIsHovered] = useState(false);
  const [storeAovLiftIsHovered, setStoreAovLiftIsHovered] = useState(false);

  let group_by = ReportsHelpers.getGroupBy(timeframeType);
  let range_start = props.timeframeTypes.filter(
    (t) => t.value == timeframeType
  )[0].range_start;
  let range_end = props.timeframeTypes.filter(
    (t) => t.value == timeframeType
  )[0].range_end;

  const isCustomTimeframe = timeframeType === "Date Range";
  const timeframeRangeDays = (() => {
    const start = new Date(range_start);
    const end = new Date(range_end);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return 0;
    }

    return (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  })();
  const showTimeframeLimitAlert = isCustomTimeframe || timeframeRangeDays > 90;

  if (timeframeType == "Date Range") {
    range_start = rangeStartValue;
    range_end = rangeEndValue;
    group_by = ReportsHelpers.getGroupBy(null, range_start, range_end);
  }

  const comparisonRange = ReportsHelpers.getComparisonRange(
    range_start,
    range_end
  );

  const liveCampaignsReport = (
    <a
      href={`/websites/${props.website.id}/cta?sort_by=created_at_latest&cta_type=&status=active&group_ids=&name=&list_type=card`}
      className="min-w-[175px] m-3 p-7 left text-black hover:text-blue-600 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-shadow"
    >
      <ReportCount
        label="Live Campaigns"
        tooltip="Number of active campaigns updated in the last 7 days with views"
        count={props.live_campaigns_count || 0}
      />
    </a>
  );

  const viewsReport = (
    <a
      href={`/websites/${props.website.id}/insights`}
      className="min-w-[175px] m-3 p-7 text-black hover:text-blue-600 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-shadow"
    >
      <ReportCount
        label="Funnel Viewers"
        tooltip="Unique viewers of all your website's funnel campaigns."
        path={`/websites/${props.website.id}/events_reports`}
        payload={{
          report_type: "distinct_count",
          report: {
            website_id: props.website.id,
            event_type: "View",
            range_start: range_start,
            range_end: range_end,
            variant_id: null,
            broadcast_id: null,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "distinct_count",
                report: {
                  website_id: props.website.id,
                  event_type: "View",
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                  variant_id: null,
                  broadcast_id: null,
                },
              }
            : null
        }
      />
    </a>
  );

  const contactsReport = (
    <a
      href={`/websites/${props.website.id}/insights`}
      className="min-w-[175px] m-3 p-7 left text-black hover:text-blue-600 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-shadow"
    >
      <ReportCount
        id={`website-${website.id}-subscribes`}
        label="New Contacts"
        tooltip="New contacts created during the selected timeframe"
        path={`/websites/${props.website.id}/events_reports`}
        loader={true}
        left={true}
        payload={{
          report_type: "distinct_count",
          report: {
            website_id: props.website.id,
            event_type: "Contact Created",
            range_start: range_start,
            range_end: range_end,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "distinct_count",
                report: {
                  website_id: props.website.id,
                  event_type: "Contact Created",
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                },
              }
            : null
        }
      />
    </a>
  );

  const ordersReport = (
    <a
      href={`/websites/${props.website.id}/insights`}
      className="min-w-[175px] m-3 p-7 left text-black hover:text-blue-600 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-shadow"
    >
      <ReportCount
        id={`website-${website.id}-orders`}
        label="Funnel Orders"
        tooltip="Total orders that converted and then purchased within the configured attribution window"
        path={`/websites/${props.website.id}/events_reports`}
        payload={{
          report_type: "count",
          report: {
            website_id: props.website.id,
            event_type: "Order",
            contactable_type: "Cta",
            contactable_id: null,
            range_start: range_start,
            range_end: range_end,
          },
        }}
        callback={() => {
          updateConversionRate(
            `#website-${website.id}-views`,
            `#website-${website.id}-orders`,
            `#website-${website.id}-order-rate`
          );

          updateAOV(
            `#website-${website.id}-orders`,
            `#website-${website.id}-revenue`,
            `#website-${website.id}-aov`,
            { comparison_expected: !!comparisonRange }
          );
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "count",
                report: {
                  website_id: props.website.id,
                  event_type: "Order",
                  contactable_type: "Cta",
                  contactable_id: null,
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                },
              }
            : null
        }
      />
    </a>
  );

  const revenueReport = (
    <a
      href={`/websites/${props.website.id}/insights`}
      className="min-w-[175px] m-3 p-7 left text-black hover:text-blue-600 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-shadow"
    >
      <ReportCount
        id={`website-${website.id}-revenue`}
        label="Funnel Revenue"
        tooltip="Revenue from orders that converted with a campaign and then purchased within the configured attribution window"
        path={`/websites/${props.website.id}/events_reports`}
        payload={{
          report_type: "revenue",
          report: {
            website_id: props.website.id,
            event_type: "Order",
            contactable_type: "Cta",
            contactable_id: null,
            range_start: range_start,
            range_end: range_end,
          },
        }}
        callback={() => {
          updateAOV(
            `#website-${website.id}-orders`,
            `#website-${website.id}-revenue`,
            `#website-${website.id}-aov`,
            {
              // React handles the comparison badge (vs store or vs last period)
              // so we skip updateAOV's badge to avoid doubling up
              comparison_expected: false,
            }
          );
        }}
        currency={props.currency}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "revenue",
                report: {
                  website_id: props.website.id,
                  event_type: "Order",
                  contactable_type: "Cta",
                  contactable_id: null,
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                },
              }
            : null
        }
      />
    </a>
  );

  const bookingsReport = (
    <a
      href={`/websites/${props.website.id}/insights`}
      className="min-w-[175px] m-3 p-7 left text-black hover:text-blue-600 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-shadow"
    >
      <ReportCount
        id={`website-${website.id}-bookings`}
        label="Bookings"
        tooltip="Unique visitors who booked a meeting through a scheduler element"
        path={`/websites/${props.website.id}/events_reports`}
        payload={{
          report_type: "distinct_count",
          report: {
            website_id: props.website.id,
            event_type: "Booking",
            contactable_type: null,
            contactable_id: null,
            range_start: range_start,
            range_end: range_end,
            variant_id: null,
            broadcast_id: null,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "distinct_count",
                report: {
                  website_id: props.website.id,
                  event_type: "Booking",
                  contactable_type: null,
                  contactable_id: null,
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                  variant_id: null,
                  broadcast_id: null,
                },
              }
            : null
        }
      />
    </a>
  );

  const customGoalsReport = (
    <a
      href={`/websites/${props.website.id}/insights`}
      className="min-w-[175px] m-3 p-7 left text-black hover:text-blue-600 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-shadow"
    >
      <ReportCount
        id={`website-${website.id}-custom-goals`}
        label={goals.customGoalLabel}
        tooltip={`Unique visitors who completed ${goals.customGoalLabel}`}
        path={`/websites/${props.website.id}/events_reports`}
        payload={{
          report_type: "distinct_count",
          report: {
            website_id: props.website.id,
            event_type: "Custom Goal",
            contactable_type: null,
            contactable_id: null,
            range_start: range_start,
            range_end: range_end,
            variant_id: null,
            broadcast_id: null,
          },
        }}
        comparison_payload={
          comparisonRange
            ? {
                report_type: "distinct_count",
                report: {
                  website_id: props.website.id,
                  event_type: "Custom Goal",
                  contactable_type: null,
                  contactable_id: null,
                  range_start: comparisonRange.range_start,
                  range_end: comparisonRange.range_end,
                  variant_id: null,
                  broadcast_id: null,
                },
              }
            : null
        }
      />
    </a>
  );

  const funnelAOVReport = (
    <a
      href={`/websites/${props.website.id}/insights`}
      className="min-w-[175px] m-3 p-7 left text-black hover:text-blue-600 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-shadow"
      data-funnel-aov-card
    >
      <div className="row hard-center">
        <div className="row hard-center">
          <span className="field-title">Funnel AOV</span>
          <Tooltip
            tooltip={
              "Average revenue of those who converted with from a funnel campaign and then purchased within the configured attribution window"
            }
            inline={true}
          />
        </div>
        <div className="row hard-center gap-2">
          <h4 className="report-count bold" style={{ margin: "5px 0px 0px" }}>
            {props.currency}
            <span id={`website-${website.id}-aov`}>
              {currentAOV.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </h4>
          {aovPercentageChange !== null && (
            <span
              className={`inline-flex items-center px-2 py-1 rounded text-base font-medium ${
                aovPercentageChange > 0
                  ? "bg-green-100 text-green-800"
                  : aovPercentageChange < 0
                  ? "bg-red-100 text-red-800"
                  : "bg-gray-100 text-gray-800"
              }`}
              onMouseEnter={() => {
                setAovLiftIsHovered(true);
              }}
              onMouseLeave={() => {
                setAovLiftIsHovered(false);
              }}
            >
              {aovPercentageChange > 0 ? (
                <svg
                  className="w-4 h-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  style={{ transform: "rotate(-45deg)" }}
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : aovPercentageChange < 0 ? (
                <svg
                  className="w-4 h-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  style={{ transform: "rotate(45deg)" }}
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : null}
              {Math.abs(aovPercentageChange)}%
              {props.shopify_revenue !== null &&
              props.shopify_revenue !== undefined &&
              props.shopify_orders !== null &&
              props.shopify_orders !== undefined ? (
                <span className="ml-1">
                  vs store ({props.currency}
                  <span id={`website-${website.id}-store-aov`}>
                    {storeAOV.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </span>
                  )
                </span>
              ) : (
                <span
                  className="ml-1"
                  style={{
                    display: aovLiftIsHovered ? "inline-block" : "none",
                  }}
                >
                  vs last period
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </a>
  );

  const storeAOVReport = (
    <a
      href={`/websites/${props.website.id}/insights`}
      className="min-w-[175px] m-3 p-7 left text-black hover:text-blue-600 border-solid border-[1px] border-slate-200 rounded-xl shadow-sm cursor-pointer hover:shadow-md transition-shadow"
    >
      <div className="row hard-center">
        <div className="row hard-center text-nowrap">
          <span className="field-title">Store AOV This Month</span>
          <Tooltip
            tooltip={"Average order value from all<br> store orders"}
            inline={true}
          />
        </div>
        <div className="row hard-center gap-2">
          <h4 className="report-count bold" style={{ margin: "5px 0px 0px" }}>
            {props.currency}
            <span id={`website-${website.id}-store-aov`}>
              {storeAOV.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </h4>
        </div>
      </div>
    </a>
  );

  useEffect(() => {
    $(rangeStartInput.current)
      .datetimepicker({
        defaultDate: new Date().setHours(0, 0, 0, 0),
      })
      .off("dp.change")
      .on("dp.change", function (e) {
        const value = utcDayStart(pickerDate(e));
        if (!value) return;
        setRangeStartValue(value);
      });

    $(rangeEndInput.current)
      .datetimepicker({
        defaultDate: new Date().setHours(23, 59, 59, 999),
      })
      .off("dp.change")
      .on("dp.change", function (e) {
        const value = utcDayEnd(pickerDate(e));
        if (!value) return;
        setRangeEndValue(value);
      });
  }, [metric, timeframeType, rangeStartValue, rangeEndValue]);

  useEffect(() => {
    const calculateAOV = () => {
      const ordersSpan = $(`#website-${website.id}-orders`);
      const revenueSpan = $(`#website-${website.id}-revenue`);

      if (
        ordersSpan.attr("data-loading") == "false" &&
        revenueSpan.attr("data-loading") == "false"
      ) {
        const orders = readReportCount(ordersSpan);
        const revenue = readReportCount(revenueSpan);
        const aov = orders && revenue ? revenue / orders : 0;
        setCurrentAOV(aov);

        // Get comparison values from data attributes set by ReportCount
        const ordersComparisonCount = parseInt(
          ordersSpan.attr("data-comparison-count") || "0"
        );
        const revenueComparisonCount = parseFloat(
          revenueSpan.attr("data-comparison-count") || "0"
        );
        const compAov =
          ordersComparisonCount && revenueComparisonCount
            ? revenueComparisonCount / ordersComparisonCount
            : 0;
        setComparisonAOV(compAov);
      }
    };

    const intervalId = setInterval(calculateAOV, 500);
    return () => clearInterval(intervalId);
  }, [website.id, timeframeType, rangeStartValue, rangeEndValue]);

  useEffect(() => {
    const hasShopifyIntegration =
      props.shopify_revenue !== null &&
      props.shopify_revenue !== undefined &&
      props.shopify_orders !== null &&
      props.shopify_orders !== undefined;

    if (hasShopifyIntegration) {
      // Compare vs store AOV if Shopify integration exists
      if (storeAOV !== null && currentAOV !== null && storeAOV !== 0) {
        const change = ((currentAOV - storeAOV) / storeAOV) * 100;
        setAovPercentageChange(Math.round(change));
      } else if (storeAOV === 0 && currentAOV > 0) {
        setAovPercentageChange(100);
      } else if (storeAOV !== null && currentAOV !== null) {
        setAovPercentageChange(0);
      }
    } else {
      // Compare vs previous period if no Shopify integration
      if (
        comparisonAOV !== null &&
        currentAOV !== null &&
        comparisonAOV !== 0
      ) {
        const change = ((currentAOV - comparisonAOV) / comparisonAOV) * 100;
        setAovPercentageChange(Math.round(change));
      } else if (comparisonAOV === 0 && currentAOV > 0) {
        setAovPercentageChange(100);
      } else if (comparisonAOV !== null && currentAOV !== null) {
        setAovPercentageChange(0);
      }
    }
  }, [
    currentAOV,
    storeAOV,
    comparisonAOV,
    props.shopify_revenue,
    props.shopify_orders,
  ]);

  useEffect(() => {
    if (props.shopify_revenue && props.shopify_orders) {
      const revenue = parseFloat(props.shopify_revenue) || 0;
      const orders = parseInt(props.shopify_orders) || 0;
      const aov = orders > 0 ? revenue / orders : 0;
      setStoreAOV(aov);
    }
  }, [props.shopify_revenue, props.shopify_orders]);

  useEffect(() => {
    if (props.shopify_revenue_last_month && props.shopify_orders_last_month) {
      const revenue = parseFloat(props.shopify_revenue_last_month) || 0;
      const orders = parseInt(props.shopify_orders_last_month) || 0;
      const aov = orders > 0 ? revenue / orders : 0;
      setStoreAOVLastMonth(aov);
    }
  }, [props.shopify_revenue_last_month, props.shopify_orders_last_month]);

  useEffect(() => {
    if (
      storeAOVLastMonth !== null &&
      storeAOV !== null &&
      storeAOVLastMonth !== 0
    ) {
      const change = ((storeAOV - storeAOVLastMonth) / storeAOVLastMonth) * 100;
      setStoreAovPercentageChange(Math.round(change));
    } else if (storeAOVLastMonth === 0 && storeAOV > 0) {
      setStoreAovPercentageChange(100);
    } else if (storeAOVLastMonth !== null && storeAOV !== null) {
      setStoreAovPercentageChange(0);
    }
  }, [storeAOV, storeAOVLastMonth]);

  // Remove any comparison badge that updateAOV may have injected (React renders its own)
  useEffect(() => {
    const card = document.querySelector("[data-funnel-aov-card]");
    if (!card) return;
    const removeInjectedBadge = () => {
      const badge = card.querySelector(".aov-comparison-badge");
      if (badge) {
        const wrapper = badge.closest(".row.hard-center");
        if (wrapper && !wrapper.classList.contains("gap-2")) wrapper.remove();
      }
    };
    removeInjectedBadge();
    const id = setInterval(removeInjectedBadge, 500);
    const timeoutId = setTimeout(() => clearInterval(id), 10000);
    return () => {
      clearInterval(id);
      clearTimeout(timeoutId);
    };
  }, [website.id]);

  return (
    <React.Fragment>
      <div className="row hard-center smooth WebsiteConversionReport">
        {showTimeframeLimitAlert && (
          <div className="row hard-center px-[15px]">
            <div className="col-xs-12 some-vertical-space border-solid border-[1px] border-black text-black pt-6 pb-2">
              <p className="smooth semibold">
                Site-wide timeframe reports for subscribes/contacts and
                orders/revenue/AOV are limited to 2026+ with improved accuracy.
              </p>
            </div>
          </div>
        )}
        <div className="row hard-center">
          <div>
            <div className="col-xs-6 no-padding-right pt-3">
              <h4 className="bold inline">Funnel Performance</h4>
            </div>
            <div className="col-xs-6 no-padding-left pt-3">
              <div className="flex flex-row items-center justify-end gap-2 flex-nowrap">
                {timeframeType == "Date Range" && (
                  <div
                    className="input-group date range-end"
                    ref={rangeEndInput}
                    style={{ width: "130px" }}
                  >
                    <input
                      type="text"
                      className="text-field cursor-default"
                      placeholder="End"
                      value={utcDayLabel(rangeEndValue)}
                      style={{ borderRadius: "3px 0px 0px 3px" }}
                      onChange={(event) => {
                        const value = utcDayEnd(event.target.value);
                        if (!value) return;
                        setRangeEndValue(value);
                      }}
                    />
                    <span className="input-group-addon !border-slate-200 !bg-white !text-black">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M5.35714 5.92632H12.5859M4.86384 1.82812V3.05773M12.9844 1.82812V3.05758M15.375 6.05758L15.375 13.1719C15.375 14.8287 14.0319 16.1719 12.375 16.1719H5.625C3.96815 16.1719 2.625 14.8287 2.625 13.1719V6.05758C2.625 4.40073 3.96815 3.05758 5.625 3.05758H12.375C14.0319 3.05758 15.375 4.40073 15.375 6.05758Z"
                          stroke="black"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                )}
                {timeframeType == "Date Range" && (
                  <div
                    className="input-group date range-start"
                    ref={rangeStartInput}
                    style={{ width: "130px" }}
                  >
                    <input
                      type="text"
                      className="text-field cursor-default"
                      placeholder="Start"
                      value={utcDayLabel(rangeStartValue)}
                      style={{ borderRadius: "3px 0px 0px 3px" }}
                      onChange={(event) => {
                        const value = utcDayStart(event.target.value);
                        if (!value) return;
                        setRangeStartValue(value);
                      }}
                    />
                    <span className="input-group-addon !border-slate-200 !bg-white !text-black">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                      >
                        <path
                          d="M5.35714 5.92632H12.5859M4.86384 1.82812V3.05773M12.9844 1.82812V3.05758M15.375 6.05758L15.375 13.1719C15.375 14.8287 14.0319 16.1719 12.375 16.1719H5.625C3.96815 16.1719 2.625 14.8287 2.625 13.1719V6.05758C2.625 4.40073 3.96815 3.05758 5.625 3.05758H12.375C14.0319 3.05758 15.375 4.40073 15.375 6.05758Z"
                          stroke="black"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                )}
                {false == (metric == "revenue" && revenueType == "total") && (
                  <div className="timeframe-date-selector !float-none !mt-0 shrink-0">
                    <select
                      className="text-field !h-[34px] !py-[6px] !px-[10px] !leading-[20px] box-border"
                      style={{ width: "100%", float: "none" }}
                      onChange={(event) => setTimeframeType(event.target.value)}
                      name="timeframe"
                      defaultValue={
                        props.timeframeTypes.filter(
                          (option) => option.value == "Last 30 Days"
                        )[0].value
                      }
                    >
                      {props.timeframeTypes.map((option) => (
                        <option
                          value={option.value}
                          key={`timeframe-option-${option.value}`}
                        >
                          {[
                            "Today",
                            "Yesterday",
                            "This Week",
                            "This Month",
                            "This Quarter",
                            "This Year",
                          ].includes(option.value)
                            ? `${option.value} (Timezone: UTC)`
                            : option.value == "Date Range"
                            ? "Date Range (UTC)"
                            : option.value}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <a
                  className="insights-link btn toolbar-btn field-title !border-slate-200 bg-white !text-black shrink-0 !h-[34px] !inline-flex !items-center !py-0 !px-3"
                  href={`/websites/${props.website.id}/insights`}
                  style={{ whiteSpace: "nowrap" }}
                >
                  All Insights →
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="row hard-center pl-3">
          <div
            className="mt-5 flex flex-row align-items-center justify-content-center gap-3 overflow-x-auto"
            style={{ marginRight: "-30px" }}
          >
            {liveCampaignsReport}
            {viewsReport}
            {contactsReport}
            {goals.checkouts && ordersReport}
            {goals.checkouts && revenueReport}
            {goals.checkouts && funnelAOVReport}
            {goals.bookings && bookingsReport}
            {goals.custom && customGoalsReport}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default WebsiteDashboardReport;
